// Shared client-side validators for the Employee Form fields.
//
// These rules mirror the server-side checks in
// Hafnia.EmployeeApi/Hafnia.Employee.Data/Validation/EmployeeFieldValidator.cs.
// Treat the server as the source of truth; the rules here only exist for UX.

const NAME_REGEX = /^(?=.*\p{L})[\p{L}\p{M}\u2019' .\-]+$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const ZIP_REGEX = /^[A-Za-z0-9][A-Za-z0-9 \-]{1,11}$/;

export const validateName = (label) => (value) => {
  if (!value || !value.toString().trim()) return `${label} is required.`;
  if (value.length > 100) return `${label} must be at most 100 characters.`;
  if (!NAME_REGEX.test(value)) {
    return `${label} may only contain letters, spaces, hyphens, apostrophes and periods.`;
  }
  return null;
};

export const validateEmail = (value, label = "Private e-mail") => {
  if (!value || !value.toString().trim()) return `${label} is required.`;
  if (value.length > 100) return `${label} must be at most 100 characters.`;
  if (!EMAIL_REGEX.test(value)) {
    return "Please enter a valid e-mail address (e.g. name@example.com).";
  }
  return null;
};

export const validateZip = (value, label = "Zip/Postal") => {
  if (!value || !value.toString().trim()) return `${label} is required.`;
  if (!ZIP_REGEX.test(value)) {
    return `${label} must be 2-12 characters and contain only letters, digits, spaces or hyphens.`;
  }
  return null;
};

export const validateAddressLine = (value, label = "Address Line 1") => {
  if (!value || !value.toString().trim()) return `${label} is required.`;
  if (value.length > 35) return `${label} must be at most 35 characters.`;
  return null;
};

export const validateCity = (value, label = "City") => {
  if (!value || !value.toString().trim()) return `${label} is required.`;
  if (value.length > 30) return `${label} must be at most 30 characters.`;
  return null;
};

export const validateRequired = (value, label) => {
  if (!value || !value.toString().trim()) return `${label} is required.`;
  return null;
};

export const validatePhone = (value, countryCallingCode, label = "Private phone") => {
  if (!value || !value.toString().trim()) return `${label} is required.`;
  const ccc = parseInt(countryCallingCode, 10) || 0;
  if (ccc <= 0) return "A country calling code is required for the phone number.";
  const digitsOnly = value.toString().replace(/\D/g, "");
  if (digitsOnly.length < 7) return `${label} looks too short to be a real number.`;
  return null;
};

/**
 * Composite client-side validation for the Save Employee Information action.
 * Mirrors `SaveEmployeeDtoValidator.Validate` on the server.
 *
 * Returns an array of error message strings (one per failing rule).
 */
export const validateSaveForm = ({
  firstName,
  lastName,
  privateEmail,
  privatePhone,
  countryCallingCode,
  addressLine1,
  city,
  zip,
  country,
  isoCountryCode,
}) => {
  const errors = [];
  const push = (msg) => { if (msg) errors.push(msg); };

  push(validateName("First name")(firstName));
  push(validateName("Last name")(lastName));
  push(validateEmail(privateEmail));
  push(validatePhone(privatePhone, countryCallingCode));
  push(validateAddressLine(addressLine1));
  push(validateCity(city));
  push(validateZip(zip));
  push(validateRequired(country, "Country"));
  push(validateRequired(isoCountryCode, "Country code"));

  return errors;
};

/**
 * Composite client-side validation for the Submit Form action.
 * Mirrors `SubmitFormValidator.Validate` on the server.
 *
 * Returns an array of error message strings (one per failing rule).
 */
export const validateSubmitForm = ({
  firstName,
  lastName,
  privateEmail,
  privatePhone,
  countryCallingCode,
  addressLine1,
  city,
  zip,
  country,
  isoCountryCode,
  bankDataCount,
  fileCount,
}) => {
  const errors = validateSaveForm({
    firstName,
    lastName,
    privateEmail,
    privatePhone,
    countryCallingCode,
    addressLine1,
    city,
    zip,
    country,
    isoCountryCode,
  });

  if (!bankDataCount || bankDataCount <= 0) {
    errors.push("At least one bank account must be added before submitting.");
  }
  if (!fileCount || fileCount <= 0) {
    errors.push("At least one supporting file must be uploaded before submitting.");
  }

  return errors;
};
